import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { MetroStats, StationStats } from '../types';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Train,
  LocationOn,
  Timeline,
  TrendingUp,
  TrendingDown,
  People,
  Speed,
  CheckCircle,
  Warning,
  Error,
  DirectionsBus,
  Assessment
} from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Analytics: React.FC = () => {
  const [metroStats, setMetroStats] = useState<MetroStats | null>(null);
  const [stationStats, setStationStats] = useState<StationStats[]>([]);
  const [lineStats, setLineStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('30_DAYS');

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, stationStatsRes, lineStatsRes] = await Promise.all([
        analyticsAPI.getMetroStats(),
        analyticsAPI.getStationStats(),
        analyticsAPI.getLineStats()
      ]);
      setMetroStats(statsRes.data);
      setStationStats(stationStatsRes.data);
      setLineStats(lineStatsRes.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (event: SelectChangeEvent<string>) => {
    setTimeRange(event.target.value);
  };

  const getMetricIcon = (metric: string) => {
    const icons: Record<string, React.ReactElement> = {
      stations: <LocationOn sx={{ fontSize: 40, color: 'primary.main' }} />,
      lines: <Timeline sx={{ fontSize: 40, color: 'secondary.main' }} />,
      routes: <DirectionsBus sx={{ fontSize: 40, color: 'success.main' }} />,
      users: <People sx={{ fontSize: 40, color: 'info.main' }} />
    };
    return icons[metric] || <Assessment sx={{ fontSize: 40, color: 'grey.500' }} />;
  };

  const getPerformanceColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !metroStats) {
    return (
      <Alert severity="error" sx={{ mx: 3, mb: 3 }}>
        {error || 'No data available'}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Analytics Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            label="Time Range"
          >
            <MenuItem value="7_DAYS">Last 7 Days</MenuItem>
            <MenuItem value="30_DAYS">Last 30 Days</MenuItem>
            <MenuItem value="90_DAYS">Last 90 Days</MenuItem>
            <MenuItem value="1_YEAR">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ px: 3, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {getMetricIcon('stations')}
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                {metroStats.totalStations.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Stations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {getMetricIcon('lines')}
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                {metroStats.totalLines.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Metro Lines
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {getMetricIcon('routes')}
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                {metroStats.activeRoutes.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Routes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {getMetricIcon('users')}
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                {metroStats.totalConnections.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Connections
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Stations */}
      <Paper sx={{ mx: 3, mb: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Stations by Connections
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Station Name</TableCell>
                <TableCell>Total Connections</TableCell>
                <TableCell>Facilities</TableCell>
                <TableCell>Amenities</TableCell>
                <TableCell>Interchange</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stationStats.slice(0, 5).map((station, index) => (
                <TableRow key={station.stationId}>
                  <TableCell>{station.stationName}</TableCell>
                  <TableCell>{station.totalConnections}</TableCell>
                  <TableCell>{station.facilitiesCount}</TableCell>
                  <TableCell>{station.amenitiesCount}</TableCell>
                  <TableCell>
                    <Chip 
                      label={station.isInterchange ? 'Yes' : 'No'} 
                      color={station.isInterchange ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Line Performance */}
      <Paper sx={{ mx: 3, mb: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Line Performance
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Line Name</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineStats.slice(0, 5).map((line, index) => (
                <TableRow key={index}>
                  <TableCell>{line.name || `Line ${index + 1}`}</TableCell>
                  <TableCell>
                    <Chip 
                      icon={<Timeline />}
                      label="Active"
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="Operational"
                      color="success"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ px: 3, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Speed sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {metroStats.interchangeStations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Interchange Stations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocationOn sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {metroStats.totalCities}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cities Covered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics; 